import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { DashboardHeader } from '@/components/dashboard/DashboardHeader';
import { useAdminDashboardStats, TimeFilter } from '@/hooks/useAdminDashboardStats';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { TrendingUp, TrendingDown, Package, CheckCircle, FileText, Truck } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const AdminDashboard = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [timeFilter, setTimeFilter] = useState<TimeFilter>('month');
  const { data: stats, isLoading } = useAdminDashboardStats(timeFilter);

  const StatCard = ({ title, icon: Icon, value, percentChange, percentVsAverage, color }: any) => (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div className={`w-12 h-12 rounded-xl ${color} flex items-center justify-center`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
      <h3 className="text-sm font-medium text-muted-foreground mb-2">{title}</h3>
      <div className="text-3xl font-bold text-foreground mb-3">{value}</div>
      <div className="flex flex-col gap-2 text-xs">
        <div className={`flex items-center gap-1 ${percentChange >= 0 ? 'text-[#14A38B]' : 'text-[#FF7171]'}`}>
          {percentChange >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
          <span>{Math.abs(percentChange).toFixed(1)}%</span>
          <span className="text-muted-foreground">{t('admin.dashboard.vsLastPeriod')}</span>
        </div>
        <div className={`flex items-center gap-1 ${percentVsAverage >= 0 ? 'text-[#14A38B]' : 'text-[#FF7171]'}`}>
          {percentVsAverage >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
          <span>{Math.abs(percentVsAverage).toFixed(1)}%</span>
          <span className="text-muted-foreground">{t('admin.dashboard.vsAverage')}</span>
        </div>
      </div>
    </Card>
  );

  return (
    <div className="space-y-8 p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <DashboardHeader 
            title={t('admin.dashboard.title')}
            userName={user?.name || user?.email}
          />
          <Select value={timeFilter} onValueChange={(v: TimeFilter) => setTimeFilter(v)}>
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="day">{t('admin.dashboard.filters.day')}</SelectItem>
              <SelectItem value="week">{t('admin.dashboard.filters.week')}</SelectItem>
              <SelectItem value="month">{t('admin.dashboard.filters.month')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-48" />)}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <StatCard
                title={t('admin.dashboard.revenue')}
                icon={TrendingUp}
                value={`${stats?.revenue.current.toFixed(0)} €`}
                percentChange={stats?.revenue.percentChange || 0}
                percentVsAverage={stats?.revenue.percentVsAverage || 0}
                color="bg-[#0880AE]"
              />
              <StatCard
                title={t('admin.dashboard.ordersDelivered')}
                icon={CheckCircle}
                value={stats?.ordersDelivered.current}
                percentChange={stats?.ordersDelivered.percentChange || 0}
                percentVsAverage={stats?.ordersDelivered.percentVsAverage || 0}
                color="bg-[#14A38B]"
              />
              <StatCard
                title={t('admin.dashboard.ordersPosted')}
                icon={FileText}
                value={stats?.ordersPosted.current}
                percentChange={stats?.ordersPosted.percentChange || 0}
                percentVsAverage={stats?.ordersPosted.percentVsAverage || 0}
                color="bg-[#37699E]"
              />
              <StatCard
                title={t('admin.dashboard.ordersInProgress')}
                icon={Truck}
                value={stats?.ordersInProgress.current}
                percentChange={stats?.ordersInProgress.percentChange || 0}
                percentVsAverage={stats?.ordersInProgress.percentVsAverage || 0}
                color="bg-[#FFBF07]"
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4">{t('admin.dashboard.revenueChart')}</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={stats?.chartData}>
                    <defs>
                      <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#0880AE" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#0880AE" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#DBE2EA" />
                    <XAxis dataKey="date" stroke="#5A6B7A" />
                    <YAxis stroke="#5A6B7A" />
                    <Tooltip />
                    <Area type="monotone" dataKey="revenue" stroke="#0880AE" fillOpacity={1} fill="url(#colorRevenue)" />
                  </AreaChart>
                </ResponsiveContainer>
              </Card>

              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4">{t('admin.dashboard.ordersChart')}</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={stats?.chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#DBE2EA" />
                    <XAxis dataKey="date" stroke="#5A6B7A" />
                    <YAxis stroke="#5A6B7A" />
                    <Tooltip />
                    <Line type="monotone" dataKey="orders" stroke="#37699E" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </Card>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
